import dynamic from "next/dynamic";
import { useInfiniteQuery, useQuery } from "react-query";
import shopService from "services/shop";
import { useCallback, useEffect, useRef } from "react";
import categoryService from "services/category";
import useUserLocation from "hooks/useUserLocation";
import useLocale from "hooks/useLocale";
import qs from "qs";
import { useRouter } from "next/router";
import LoadMore from "components/button/load-more";

const ZoneNotFound = dynamic(
  () => import("components/zoneNotFound/zoneNotFound"),
);
const ShopList = dynamic(() => import("containers/shopList/shopList"));
const ShopCategoryList = dynamic(
  () => import("containers/shopCategoryList/v1"),
);

export default function ShopsPage() {
  const { t, locale } = useLocale();
  const { query } = useRouter();
  const loader = useRef(null);
  const location = useUserLocation();

  const { isSuccess: isInsideZone, isLoading: isZoneLoading } = useQuery(
    ["shopZones", location],
    () =>
      shopService.checkZone({
        address: location,
      }),
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery(
      ["shops", locale, location, query?.category_id],
      ({ pageParam = 1 }) =>
        shopService.getAllShops(
          qs.stringify({
            page: pageParam,
            perPage: 12,
            address: location,
            category_id: query?.category_id || undefined,
          }),
        ),
      {
        getNextPageParam: (lastPage: any) => {
          if (lastPage.meta.current_page < lastPage.meta.last_page) {
            return lastPage.meta.current_page + 1;
          }
          return undefined;
        },
      },
    );

  const { data: shopCategoryList, isLoading: shopCategoryLoading } = useQuery(
    ["shopCategories", locale],
    () => categoryService.getAllShopCategories({ perPage: 20 }),
  );

  const handleObserver = useCallback((entries: any) => {
    const target = entries[0];
    if (target.isIntersecting && hasNextPage) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 0,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) observer.observe(loader.current);
  }, [handleObserver]);

  return (
    <>
      <ShopCategoryList
        data={shopCategoryList?.data?.sort((a, b) => a?.input - b?.input) || []}
        loading={shopCategoryLoading}
      />
      <br />
      <ShopList
        title={t("stores")}
        shops={data?.pages?.flatMap((item) => item.data) || []}
        loading={isLoading && !isFetchingNextPage}
      />

      {hasNextPage && (
        <>
          <br />
          <LoadMore onClick={fetchNextPage} loading={isFetchingNextPage}>
            {t("load.more")}
          </LoadMore>
        </>
      )}

      {!isInsideZone && !isZoneLoading && <ZoneNotFound />}
    </>
  );
}
